import type { ContentViewKind, UserContentView } from '@doneverse/shared'
import { api } from '../api/index.js'

interface ListResponse {
  views: UserContentView[]
}

export const contentViewsService = {
  /**
   * Record (or refresh) the calling user's view of one piece of polymorphic
   * content. First call inserts; later calls bump last_viewed_at + view_count.
   */
  record: async (input: { content_type: ContentViewKind; content_id: string }): Promise<UserContentView> => {
    return api.post(input, '/v1/content-views').json<UserContentView>()
  },

  /**
   * Report how much of one piece of content the user has watched. The server
   * keeps the high-water mark, so an out-of-order or replayed ping is harmless
   * — callers throttle these rather than trying to order them.
   */
  progress: async (input: {
    content_type: ContentViewKind
    content_id: string
    percent: number
  }): Promise<UserContentView> => {
    return api.post(input, '/v1/content-views/progress').json<UserContentView>()
  },

  /**
   * Fetch the calling user's view records for a content_type. Pass
   * `contentIds` to restrict to a batch — typical use is "which of these
   * lessons has the user viewed?" on a catalog/sidebar render.
   */
  list: async (input: {
    content_type: ContentViewKind
    content_ids?: string[]
  }): Promise<UserContentView[]> => {
    const params = new URLSearchParams({ content_type: input.content_type })
    if (input.content_ids && input.content_ids.length > 0) {
      params.set('content_ids', input.content_ids.join(','))
    }
    const res = await api.get(`/v1/content-views?${params.toString()}`).json<ListResponse>()
    return res.views
  },
}
